import type { SigninRedirectArgs, UserManagerSettings } from "oidc-client-ts"
import type { ServerResponseV1 } from "@/http"
import type { IUserResponseV1 } from "@/types"
import * as Sentry from "@sentry/vue"
import { UserManager, WebStorageStateStore } from "oidc-client-ts"
import { defineStore } from "pinia"
import { computed, ref } from "vue"
import { v1 } from "@/axios"
import { ForbiddenException, UnauthorisedException } from "@/http"
import { User } from "@/models"

const config: UserManagerSettings = {
  authority: import.meta.env.VITE_OPENID_AUTHORITY ?? "https://localhost:44339",
  client_id: import.meta.env.VITE_OPENID_CLIENT_ID ?? "gpx-web",
  redirect_uri:
    import.meta.env.VITE_OPENID_REDIRECT_URI
      ?? "http://localhost:5173/callback",
  post_logout_redirect_uri:
    import.meta.env.VITE_OPENID_POST_LOGOUT_REDIRECT_URI
      ?? "http://localhost:5173/login",
  silent_redirect_uri:
    import.meta.env.VITE_OPENID_SILENT_REDIRECT_URI
      ?? "http://localhost:5173/silent-renew",
  response_type: "code",
  scope: "api offline_access",
  automaticSilentRenew: true,
  userStore: new WebStorageStateStore({ store: localStorage }),
}

const userManager = new UserManager(config)

export const useAuthStore = defineStore("auth", () => {
  const user = ref<User | null>(null)

  const setUser = (newUser: User | null) => {
    user.value = newUser
  }

  const getUser = async () => {
    try {
      const response = await v1.get<ServerResponseV1<IUserResponseV1>>("/me")
      const currentUser = User.fromResponseV1(response.data.data)

      setUser(currentUser)

      return currentUser
    }
    catch (err: unknown) {
      if (
        err instanceof UnauthorisedException
        || err instanceof ForbiddenException
      ) {
        setUser(null)

        return null
      }

      Sentry.captureException?.(err)

      return null
    }
  }

  const getAccessToken = async () => {
    const tokenState = await userManager.getUser()

    if (!tokenState) {
      return null
    }

    if (tokenState?.expired) {
      try {
        await userManager.signinSilent()
      }
      catch {
        setUser(null)

        return null
      }
    }

    return tokenState?.access_token
  }

  const redirectToLogin = async (redirect?: string) => {
    const args = { state: {} } satisfies SigninRedirectArgs

    if (redirect) {
      Object.assign(args.state, { redirect })
    }

    return userManager.signinRedirect(args)
  }

  const initialize = async () => {
    await getUser()
  }

  const handleCallback = () => {
    return userManager.signinRedirectCallback()
  }

  const handleSilentCallback = () => userManager.signinSilentCallback()

  const signinSilent = () => userManager.signinSilent()

  const logout = () =>
    userManager.signoutRedirect({
      post_logout_redirect_uri: config.post_logout_redirect_uri,
    })

  const isAuthenticated = computed(() => Boolean(user.value))

  return {
    user,
    isAuthenticated,
    initialize,
    redirectToLogin,
    logout,
    signinSilent,
    handleCallback,
    handleSilentCallback,
    getUser,
    setUser,
    getAccessToken,
    userManager,
  }
})
